/**
 * 共通
 */

import dayjs from "dayjs";

export default {
  toDate,
  formatDate,
  formatDate24,
  addDate,
  subDays,
  subMinutes,
  subMonth,
  getEve10,
  roundMinutes,
  checkShortInterval,
  getDatePath,
  getDateConnectPath,
  getInfoLvl,
  formatFloat,
};

// 内容コード無効時の表示文字列
const invalidStr = "-- ";

/**
 * Date型生成
 * @param {*} date 日時文字列
 */
function toDate(date) {
  if (!date) date = new Date();
  return dayjs(date).toDate();
}

/**
 * 日付フォーマット
 * @param {*} date 変換対象の日付
 * @param {*} format フォーマット
 */
function formatDate(date, format) {
  if (!format) format = "YYYY/MM/DD HH:mm";
  return dayjs(date).format(format);
}

/**
 * 日付フォーマット(0時00分は前日の24時00分として扱う)
 * @param {*} date 変換対象の日付
 * @param {*} format フォーマット
 */
function formatDate24(date, format) {
  if (!format) format = "YYYY/MM/DD HH:mm";
  // 0時00分チェック
  if (dayjs(date).format("HHmm") === "0000") {
    // 一日前にして'HH'部分を'24'に書き換える
    let day24 = dayjs(date).add(-1, "days");
    format = format.replace("HH", "24");
    return day24.format(format);
  }
  return dayjs(date).format(format);
}

/**
 * 指定日時加算
 * @param {*} date 対象日付
 * @param {*} days 日数
 * @param {*} hours 時間
 * @param {*} minutes 分
 */
function addDate(date, days, hours, minutes) {
  return dayjs(date)
    .add(days, "day")
    .add(hours, "hour")
    .add(minutes, "minute")
    .toDate();
}

/**
 * 指定日差分
 * @param {*} date 対象日付
 * @param {*} days 日数
 */
function subDays(date, days) {
  return dayjs(date).subtract(days, "day");
}

/**
 * 指定分差分
 * @param {*} date 対象日付
 * @param {*} minutes 分
 */
function subMinutes(date, minutes) {
  return dayjs(date).subtract(minutes, "minute");
}

/**
 * 指定月差分
 * @param {*} date 対象日付
 * @param {*} month 月
 */
function subMonth(date, month) {
  return dayjs(date).subtract(month, "month");
}

/**
 * 指定時間から指定期間分前までの10分単位のリストを取得する
 * @param {*} date 対象日付
 * @param {*} period 期間(分)
 * @param {*} order 昇順(asc) or 降順(desc)
 * @param {*} format フォーマット
 */
function getEve10(date, period, order, format) {
  if (!format) format = "YYYY/MM/DD HH:mm";
  var formEndTime = formatDate(date, format);
  // 10分単位の時刻を取得
  var EndTime = formEndTime.substring(0, formEndTime.length - 1) + "0";
  var startTime = formatDate(subMinutes(EndTime, period).toDate(), format);
  var result = [];

  while (startTime <= EndTime) {
    result.push(startTime);
    // 10分後に進む
    startTime = formatDate(addDate(startTime, 0, 0, 10), format);
  }

  if (order === "desc") {
    // 降順に並び替え
    result.sort(function(a, b) {
      if (a > b) return -1;
      if (a < b) return 1;
      return 0;
    });
  }

  return result;
}

/**
 * 時刻（分）を5分or10分間隔に丸める
 * @param {*} date 対象日時
 * @param {*} shortIntervalDay 5分間隔対象期間
 * @param {*} crnt 最新観測日時
 */
function roundMinutes(date, shortIntervalDay, crnt) {
  // 秒以下を覗く
  let roundDate = dayjs(date)
    .startOf("minute")
    .toDate();
  let crntDate = dayjs()
    .startOf("minute")
    .toDate();
  if (crnt) {
    crntDate = crnt;
  }
  // 対象日時が最新観測日時よりも未来の場合、最新観測日時を使用する
  if (roundDate.getTime() > crntDate.getTime()) {
    roundDate = crntDate;
  }

  if (checkShortInterval(roundDate, shortIntervalDay)) {
    // 5分間隔に切り捨て
    roundDate.setMinutes(Math.floor(roundDate.getMinutes() / 5) * 5);
  } else {
    // 10分間隔に切り捨て
    roundDate.setMinutes(Math.floor(roundDate.getMinutes() / 10) * 10);
  }
  return roundDate;
}
/**
 * 5分間隔対象日の判定
 * @param {*} date 対象日付
 * @param {*} shortIntervalDay 5分間隔対象期間
 */
function checkShortInterval(date, shortIntervalDay) {
  // 5分間隔対象日の判定は日付のみで行うため、時分秒情報を除く
  const nowDate = dayjs().startOf("date");
  const targetDate = dayjs(date).startOf("date");
  if (nowDate < targetDate.add(shortIntervalDay, "day")) {
    return true;
  }
  return false;
}

/**
 * 対象日時をもとにファイル取得用パスを生成
 * @param {*} date 対象日時
 */
function getDatePath(date) {
  if (!date) date = new Date();
  let datePath =
    formatDate(date, "YYYYMMDD") + "/" + formatDate(date, "HHmm") + "/";
  if (process.env.NODE_ENV === "development") {
    // ローカルで動作させる場合は日時を指定しない
    datePath = "";
  }
  return datePath;
}

/**
 * 対象日時をもとに画像ファイル生成用のパスを生成
 * @param {*} date 対象日時
 */
function getDateConnectPath(date) {
  if (!date) date = new Date();
  let datePath =
    formatDate(date, "YYYYMMDDHHmm");
  return datePath;
}

/**
 * お知らせの緊急度を返す
 * @param {*} summaryCode お知らせ分類
 */
function getInfoLvl(summaryCode) {
  const EMERGENCY = 4;
  const MISSING = 3;
  const MAINTENANCE = 2;
  const OTHER = 1;

  if (summaryCode == 4 || summaryCode == 6) {
    return EMERGENCY;
  } else if (summaryCode == 2 || summaryCode == 3 || summaryCode == 7) {
    return MISSING;
  } else if (summaryCode == 1 || summaryCode == 5) {
    return MAINTENANCE;
  }
  return OTHER;
}

/**
 * 観測所情報 数値フォーマット
 *
 * @param {?number} val1 数値
 * @param {number} digit 小数点有効桁数 (省略可能)
 * @returns {string} フォーマット後の文字列(数値が null の場合 invalidStr)
 */
function formatFloat(val1, digit) {
  if (val1 === null) {
    return invalidStr;
  }
  if (digit === undefined) {
    // 小数点有効桁数の調整なし
    return val1;
  }

  const val1Str = val1.toString();
  const pattern = new RegExp("^[-]?([1-9]\\d*|0)(\\.\\d+)?$");
  if (pattern.test(val1Str)) {
    // 入力された値が数値の場合、
    // 文字列に変換して小数点有効桁数の調整を行なう
    // 小数点有効桁数に足りなければ 0 を足す
    // 例1: digit 2 の場合  1.234 → 1.23
    // 例2: digit 2 の場合  1.2 → 1.20

    // 小数点の位置を調べる
    const index = val1Str.indexOf(".");
    if (index === -1) {
      // 小数点が含まれない場合、
      if (digit > 0) {
        // 「小数点 . 」と「小数点有効桁数の 0 」を付加する
        let zeroFill = "";
        for (let i = 0; i < digit; i++) {
          zeroFill = zeroFill + "0";
        }
        return val1Str + "." + zeroFill;
      } else {
        return val1Str;
      }
    } else {
      // 小数点が含まれる場合、
      const val1Digit = val1Str.length - index - 1;
      if (val1Digit < digit) {
        // 小数点以下が 小数点有効桁数 より少ない
        // 不足分の 0 を付加する
        let zeroFill = "";
        for (let i = 0; i < digit - val1Digit; i++) {
          zeroFill = zeroFill + "0";
        }
        return val1Str + zeroFill;
      } else if (val1Digit > digit) {
        // 小数点以下が 小数点有効桁数 より多い
        // 余剰分の小数点以下を削除する
        if (digit <= 0) {
          // 小数点有効桁数 に 0 以下を指定された場合、
          // 小数点まで削除する
          return val1Str.slice(0, (val1Digit + 1) * -1);
        } else {
          return val1Str.slice(0, (val1Digit - digit) * -1);
        }
      } else {
        // 小数点以下が 小数点有効桁数 とイコール
        // そのまま返す
        return val1Str;
      }
    }
  }

  // 入力された値が数値ではない場合
  return invalidStr;
}
